import {
  CONTRACT_TYPE_PROPOSALS,
  type ContractDetail,
  type ContractTypeConfirmation,
  type ContractTypeConfirmedNewerRow,
  type ContractTypeDecisionSeen,
  type ContractTypeLineageNote,
  type ContractTypeProposal,
  type ContractTypeUndecidedRow,
  type DecideContractTypeResponse,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { refusalCodeOf } from './base-msa-shared';
import { DECISION_STALE_COPY } from './type-proposal-copy';

/**
 * The pure half of the Admin's type decision — CLM-110 (R-110-17 … R-110-22,
 * §3.5). Shared by the Type proposals page and the Contract record, which open
 * the same decision panel. Kept apart from the components for the
 * `base-msa-shared.ts` reason: a component module that also exports functions
 * breaks fast refresh.
 *
 * ## What the panel previews is a preview
 *
 * Every rule here — which actions a row offers, whether a SOW number is asked
 * for or cleared, whether the current base pre-empts the decision — is the
 * spec's, restated so the Admin sees the outcome before posting. The decision
 * endpoint re-derives every one of them from the rows it locks, and refuses
 * on its own terms (`stale_view`, `current_base_contract`, …); the panel's job
 * is that those refusals are rare and, when they come, explained in words.
 *
 * ## `seen` is never rebuilt — it is always the API's
 *
 * The `seen` block is the Admin's attestation of what they looked at
 * (R-110-17): the root's latest reading, every family row at any depth, every
 * amendment's reading. The API computes it from the same family read the
 * decision makes, on the Type proposals row and on the record's
 * `contract_type_status` alike, and the panel posts it back unchanged. Nothing
 * here derives one: a copy rebuilt from the record's other fields — its stored
 * proposal (`NULL` for an answer outside the vocabulary), its direct amendments
 * only, no amendment readings — would never match the locked rows, and every
 * decision from the record would be `409 stale_view`, Reload included.
 */

/** Everything the decision panel needs about one agreement family. */
export interface DecisionSubject {
  readonly contract_key: string;
  readonly contract_type: string;
  /**
   * CLM-118 — as the API saw it (`seen.confirmation`). `pending` means the stored
   * `OTHER` is a placeholder, so there is no type to confirm or keep; and
   * `auto_confirmed` is settled with no person, so an Admin may confirm it.
   */
  readonly confirmation: ContractTypeConfirmation;
  readonly sow_number: string | null;
  /** The agreement's latest AI reading's type: `null` for `ai_no_type`; the field absent when never read. */
  readonly reading: { readonly proposed_type: ContractTypeProposal | null } | null;
  /** The family's amendments, sorted — every row the decision also writes. */
  readonly amendment_keys: readonly string[];
  /** The current base for its type (`409 current_base_contract`), as the API read it. */
  readonly is_current_base: boolean;
  /** The record's lineage notes (§3.5): flagged by a decision, never changed. Empty from the list. */
  readonly lineage_notes: readonly ContractTypeLineageNote[];
  readonly seen: ContractTypeDecisionSeen;
  /**
   * CLM-121 (R-121-9, R-121-15) — correcting this type pauses the type's
   * auto-approval: it is a safety sample, or a type approved by policy. `null`
   * otherwise. The panel warns before such a correction.
   */
  readonly pauses_auto_approval_on: 'sample' | 'policy_approval' | null;
}

type ListedRow = ContractTypeUndecidedRow | ContractTypeConfirmedNewerRow;

/** A Type proposals row as a decision subject; its `seen` goes back unchanged. */
export function subjectOfRow(row: ListedRow, lineageNotes: readonly ContractTypeLineageNote[] = []): DecisionSubject {
  return {
    contract_key: row.contract_key,
    contract_type: row.contract_type,
    confirmation: row.seen.confirmation,
    sow_number: row.sow_number,
    reading: row.proposal === null ? null : { proposed_type: row.proposal.proposed_type },
    amendment_keys: row.amendments.map((amendment) => amendment.contract_key),
    is_current_base: row.is_current_base,
    lineage_notes: lineageNotes,
    seen: row.seen,
    pauses_auto_approval_on:
      'auto_approval_sample' in row && row.auto_approval_sample
        ? 'sample'
        : 'auto_confirmation' in row && row.auto_confirmation?.kind === 'auto_approval_policy'
          ? 'policy_approval'
          : null,
  };
}

/**
 * The record as a decision subject, or `null` when it is not decidable (an
 * amendment, or a contract the AI has not read yet). Everything comes from
 * `contract_type_status`, whose `seen` is set exactly when `decidable`: the
 * family is `seen.family_contract_keys` (every depth) and the reading is the
 * root's latest — `ai_no_type` included, which the stored proposal cannot show.
 */
export function subjectOfRecord(contract: ContractDetail): DecisionSubject | null {
  const status = contract.contract_type_status;
  const seen = status.seen;
  if (seen === null) return null;
  return {
    contract_key: contract.contract_key,
    contract_type: contract.contract_type,
    confirmation: seen.confirmation,
    sow_number: contract.sow_number,
    reading: seen.proposal_audit_id === null ? null : { proposed_type: seen.proposed_type },
    amendment_keys: seen.family_contract_keys.filter((key) => key !== contract.contract_key),
    is_current_base: status.is_current_base,
    lineage_notes: status.lineage_notes,
    seen,
    pauses_auto_approval_on: status.auto_approval_sample
      ? 'sample'
      : status.auto_confirmation?.kind === 'auto_approval_policy'
        ? 'policy_approval'
        : null,
  };
}

function isDecidableType(type: string): type is ContractTypeProposal {
  return (CONTRACT_TYPE_PROPOSALS as readonly string[]).includes(type);
}

/** The type as the subject holds it, in words: a Type pending placeholder is never printed as `OTHER` (B19). */
export function typeNowWords(subject: Pick<DecisionSubject, 'contract_type' | 'confirmation'>): string {
  return subject.confirmation === 'pending' ? 'Type pending' : subject.contract_type;
}

/** One action a row offers (§4.7): the type it sets, or `null` for Correct… and Type…, which ask. */
export interface DecisionAction {
  readonly id: 'accept' | 'keep' | 'confirm' | 'correct' | 'type';
  readonly label: string;
  readonly type: ContractTypeProposal | null;
}

/**
 * The actions a row offers: **Accept {AI type}**, **Keep {current}** and
 * **Correct…** when the AI read another type; **Accept {type}** and
 * **Correct…** when it read the same one; **Confirm {current}** and
 * **Correct…** with no usable reading (never read, restricted, or
 * `ai_no_type`, R-110-28).
 *
 * CLM-118 — two states read the confirmation first. A **Type pending** root has
 * no type to confirm or keep, so it offers **Accept {AI type}** when the reading
 * is usable and **Type…** — never "Confirm OTHER" or "Keep OTHER" (R-118-10).
 * An **auto-confirmed** type whose reading agrees offers **Confirm {type}**: the
 * file name and the AI agreed with no person, and an Admin's confirmation is a
 * decision of its own (§3.6: `no_change` does not fire on it).
 */
export function decisionActions(subject: DecisionSubject): readonly DecisionAction[] {
  const current = subject.contract_type;
  const read = subject.reading?.proposed_type ?? null;
  const correct: DecisionAction = { id: 'correct', label: 'Correct…', type: null };
  if (subject.confirmation === 'pending') {
    const type: DecisionAction = { id: 'type', label: 'Type…', type: null };
    return read === null ? [type] : [{ id: 'accept', label: `Accept ${read}`, type: read }, type];
  }
  if (subject.confirmation === 'auto_confirmed' && read === current && isDecidableType(current)) {
    return [{ id: 'confirm', label: `Confirm ${current}`, type: current }, correct];
  }
  if (read === null) {
    return isDecidableType(current) ? [{ id: 'confirm', label: `Confirm ${current}`, type: current }, correct] : [correct];
  }
  if (read === current) return [{ id: 'accept', label: `Accept ${read}`, type: read }, correct];
  return [
    { id: 'accept', label: `Accept ${read}`, type: read },
    ...(isDecidableType(current) ? [{ id: 'keep' as const, label: `Keep ${current}`, type: current }] : []),
    correct,
  ];
}

/**
 * The SOW-number rule a decision applies (§3.5): **into** SOW the request must
 * carry the number; **staying** SOW it is unchanged and none may be sent;
 * **out of** SOW every family row's number is cleared. `none` — neither side is
 * a SOW.
 */
export type SowNumberRule = 'required' | 'unchanged' | 'cleared' | 'none';

export function sowNumberRule(subject: DecisionSubject, type: ContractTypeProposal): SowNumberRule {
  const fromSow = subject.contract_type === 'SOW';
  if (type === 'SOW') return fromSow ? 'unchanged' : 'required';
  return fromSow ? 'cleared' : 'none';
}

/** R-110-21 / §3.5 — the current base cannot be retyped; confirming it as its own type is allowed. */
export function basePreempts(subject: DecisionSubject, type: ContractTypeProposal): boolean {
  return subject.is_current_base && type !== subject.contract_type;
}

/** "the agreement and 2 amendments" — the family a decision writes, in words. */
export function familyWords(amendments: number): string {
  if (amendments === 0) return 'the agreement';
  return `the agreement and ${String(amendments)} ${amendments === 1 ? 'amendment' : 'amendments'}`;
}

/** The receipt's one line: "Type MSA confirmed · audit 1234 · the agreement and 2 amendments updated · ID unchanged". */
export function receiptLine(response: DecideContractTypeResponse): string {
  return (
    `Type ${response.contract_type} confirmed · audit ${response.audit.audit_id} · ` +
    `${familyWords(response.family.length - 1)} updated · ID unchanged`
  );
}

/**
 * A refusal the panel explains in its own words. `up` — neutral — for a 409:
 * nothing failed, the state moved or the rule held. `bad` for a 400 or 422:
 * the request itself cannot be decided as sent.
 */
export interface DecisionRefusal {
  readonly code: string;
  readonly tone: 'up' | 'bad';
  readonly body: string;
  /** `stale_view` and `family_changed` — the panel offers Reload. */
  readonly reload: boolean;
}

export interface DecisionProblem {
  readonly kind: ApiProblemKind;
  /** `null` for anything this build has no words for — a 403, a 404, a 5xx — which `ErrorNotice` renders by kind. */
  readonly refusal: DecisionRefusal | null;
  readonly correlation_id: string | null;
}

const CONFLICT_COPY: Readonly<Record<string, { readonly body: string; readonly reload: boolean }>> = Object.freeze({
  stale_view: { body: DECISION_STALE_COPY, reload: true },
  family_changed: {
    body: 'An amendment was filed for this agreement while you were deciding, so nothing was written. Reload and decide again.',
    reload: true,
  },
  amendment_follows_agreement: {
    body: 'This contract amends another agreement, and its type follows that agreement. Decide the agreement instead.',
    reload: false,
  },
  awaiting_ai_proposal: {
    body: 'The AI has not read this contract yet. Decide its type once its reading lands.',
    reload: false,
  },
  sow_number_in_use: {
    body: 'That SOW number is held by another agreement, outside this family. Nothing was written.',
    reload: false,
  },
  current_base_contract: {
    body: 'This contract is the current base MSA. Replace the base on the Base MSA page first, then retype it. Nothing was written.',
    reload: false,
  },
});

const VALIDATION_COPY: Readonly<Record<string, string>> = Object.freeze({
  no_change: 'Nothing to change: the type is already confirmed as this, and no newer AI reading has arrived since.',
  sow_number_required: 'A SOW needs its number. Enter it to type this agreement as a SOW.',
  sow_number_shape: 'That is not a usable SOW number. Type it exactly as it appears on the signed document.',
  sow_number_not_applicable: 'Only a SOW carries a SOW number. Clear it, or choose SOW.',
});

/** A failed decision as the panel holds it. A code is trusted only with the status the API sends it under. */
export function decisionProblemOf(error: unknown): DecisionProblem {
  const kind = classifyApiError(error);
  const code = refusalCodeOf(error);
  let refusal: DecisionRefusal | null = null;
  if (code !== null && kind === 'conflict') {
    const copy = CONFLICT_COPY[code];
    if (copy !== undefined) refusal = { code, tone: 'up', ...copy };
  } else if (code !== null && kind === 'validation') {
    const body = VALIDATION_COPY[code];
    if (body !== undefined) refusal = { code, tone: 'bad', body, reload: false };
  }
  return { kind, refusal, correlation_id: error instanceof ApiError ? error.correlation_id : null };
}
